import { router } from "@inertiajs/react";
import {
  Button,
  Flex,
  SegmentedControl,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useState } from "react";
import type {
  SponsorsReportPreset,
  SponsorsReportRange,
} from "@/types/sponsors_report";

interface Props {
  range: SponsorsReportRange;
  reportPath: string;
  token: string;
}

const PRESETS: { value: SponsorsReportPreset; label: string }[] = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "custom", label: "Custom" },
];

// The range lives in the query string so a sponsor can bookmark or forward a
// specific view. Every change is a plain Inertia GET back to the same page.
export default function RangePicker({ range, reportPath, token }: Props) {
  const [preset, setPreset] = useState<SponsorsReportPreset>(range.preset);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);

  const choosePreset = (value: string) => {
    const next = value as SponsorsReportPreset;
    setPreset(next);
    if (next !== "custom") {
      router.get(
        reportPath,
        { t: token, range: next },
        { preserveScroll: true },
      );
    }
  };

  const applyCustom = () => {
    router.get(reportPath, { t: token, from, to }, { preserveScroll: true });
  };

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="3" wrap="wrap">
        <SegmentedControl.Root value={preset} onValueChange={choosePreset}>
          {PRESETS.map((p) => (
            <SegmentedControl.Item key={p.value} value={p.value}>
              {p.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
        <Text size="2" color="gray">
          {range.label}
        </Text>
      </Flex>

      {preset === "custom" && (
        <Flex align="end" gap="2" wrap="wrap">
          <label>
            <Text as="div" size="1" color="gray" mb="1">
              From
            </Text>
            <TextField.Root
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label>
            <Text as="div" size="1" color="gray" mb="1">
              To
            </Text>
            <TextField.Root
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <Button variant="soft" onClick={applyCustom} disabled={!from || !to}>
            Apply
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
